import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import './EditSampleBlock.css';

import { cardFinish } from '../../hoc/MtgProvider';
import { PropTypesCustom } from '../../libs/helper';
import { SimpleSelect, simpleSelectTypeCls } from '../SimpleComponents/SimpleSelect';
import SimpleTextarea from '../SimpleComponents/SimpleTextarea';
import SimpleCheckbox, { CheckboxTypes } from '../SimpleComponents/SimpleCheckbox';
import SimpleButton, { ButtonsWrapper, ButtonTypes } from '../SimpleComponents/SimpleButton';
import { buildClass } from '../../libs/classes';

const MAX_CARD_NOTE_LENGTH = 1000;

/**
 * Block for editing sample information
 * @component
 */
const EditSampleBlock = ({ card, sample, language_default, showMerge, onConfirm, onCancel }) => {
	const [qty, setQty] = useState(sample ? sample.qty : 1);
	const [condition, setCondition] = useState(sample ? sample.condition : 1);
	const [finish, setFinish] = useState(sample ? parseInt(sample.finish) : -1);
	const [note, setNote] = useState(sample ? sample.note : '');
	const [language, setLanguage] = useState(sample ? sample.language : language_default);
	const [merge, setMerge] = useState(true);

	useEffect(() => {
		console.log('Initialization: EditSampleBlock');
	}, []);

	useEffect(() => {
		if (sample) {
			setFinish(sample.finish);
			return;
		}
		// Select first available finish for selected card
		let finishUpd = -1;
		if (card.nonfoil) {
			finishUpd = cardFinish.nonfoil;
		} else if (card.foil) {
			finishUpd = cardFinish.foil;
		} else if (card.etched) {
			finishUpd = cardFinish.etched;
		}
		setFinish(finishUpd);
	}, [card, sample]);

	return (
		<div className="card-action">
			<SimpleTextarea
				className="sample-notes"
				limit={MAX_CARD_NOTE_LENGTH}
				placeholder="Any notes if required…"
				tabIndex={103}
				title="Sample notes"
				value={note}
				onChange={handleChangeCardNote}
				onKeyDown={handleKeyDown}
			/>
			<SimpleSelect
				tabIndex={104}
				value={String(condition)}
				onChange={handleChangeCondition}
				onKeyDown={handleKeyDown}
			>
				<option value="1">Near Mint (NM)</option>
				<option value="2">Light Played (LP)</option>
				<option value="3">Moderately Played (MP)</option>
				<option value="4">Heavily Played (HP)</option>
				<option value="5">Damaged (D)</option>
			</SimpleSelect>

			<SimpleSelect tabIndex={105} value={String(finish)} onChange={handleChangeFinish} onKeyDown={handleKeyDown}>
				<option disabled={!card['nonfoil']} value="0">
					Non-Foil
				</option>
				<option disabled={!card['foil']} value="1">
					Foil
				</option>
				<option disabled={!card['etched']} value="2">
					Etched
				</option>
			</SimpleSelect>

			<div className="row">
				<div className="spacer" />
				<SimpleSelect
					className={buildClass('language', simpleSelectTypeCls.thin)}
					tabIndex={106}
					value={language}
					onChange={handleChangeLanguage}
					onKeyDown={handleKeyDown}
				>
					<option value="en">English</option>
					<option value="zhs">Chinese (simpl.)</option>
					<option value="zht">Chinese (trad.)</option>
					<option value="fr">French</option>
					<option value="de">German</option>
					<option value="it">Italian</option>
					<option value="ja">Japanese</option>
					<option value="ko">Korean</option>
					<option value="pt">Portuguese</option>
					<option value="ru">Russian</option>
					<option value="es">Spanish</option>
				</SimpleSelect>
				<div className="quantity">
					<div className="minus noselect" onClick={() => handleChangeQty2(-1)}>
						&minus;
					</div>
					<input
						autoFocus
						tabIndex={106}
						value={qty}
						onChange={handleChangeQty}
						onKeyDown={handleKeydownQty}
					/>
					<div className="plus noselect" onClick={() => handleChangeQty2(1)}>
						+
					</div>
				</div>
			</div>

			{showMerge ? (
				<div className="row merge-row">
					<SimpleCheckbox
						className="merge-samples"
						label="Merge with existing"
						state={merge}
						title="Merge with existing sample with the same parameters"
						type={CheckboxTypes.TICK}
						onClick={handleChangeMerge}
					/>
				</div>
			) : null}

			<div className="row">
				<div className="spacer" />
				<ButtonsWrapper>
					{(sample && (
						<SimpleButton
							tabIndex={107}
							title="Save"
							type={ButtonTypes.DEFAULT}
							value="Save"
							onClick={handleConfirm}
							onKeyDown={handleKeyDownConfirm}
						/>
					)) || (
						<SimpleButton
							tabIndex={107}
							title="Add to Collection"
							type={ButtonTypes.DEFAULT}
							value="Add to Collection"
							onClick={handleConfirm}
							onKeyDown={handleKeyDownConfirm}
						/>
					)}
					<SimpleButton
						tabIndex={107}
						title="Cancel"
						type={ButtonTypes.GREY}
						value="Cancel"
						onClick={handleCancel}
						onKeyDown={handleKeyDownCancel}
					/>
				</ButtonsWrapper>
			</div>
		</div>
	);

	// Handling of input for note textarea
	function handleChangeCardNote(event) {
		let shortened = event.target.value.slice(0, MAX_CARD_NOTE_LENGTH);
		setNote(shortened);
	}

	// Handling of condition select
	function handleChangeCondition(event) {
		setCondition(parseInt(event.target.value));
	}

	// Handle of finish select
	function handleChangeFinish(event) {
		setFinish(parseInt(event.target.value));
	}

	// Handle of language select
	function handleChangeLanguage(event) {
		setLanguage(event.target.value);
	}

	// Handle of qty input
	function handleChangeQty(event) {
		setQty(event.target.value);
	}

	// Handle buttons for qty input increment/decrement
	function handleChangeQty2(step) {
		let qty_upd = normalizeQty(qty) + step;
		qty_upd = qty_upd > 0 ? qty_upd : 1;
		setQty(qty_upd);
	}

	// Cast qty to integer
	function normalizeQty(qty_upd) {
		return isNaN(qty_upd) ? 1 : parseInt(qty_upd);
	}

	function handleChangeMerge() {
		setMerge(!merge);
	}

	// Handle Enter as Confirm, Escape as Cancel
	function handleKeyDownConfirm(e) {
		if (e.key === 'Enter') {
			handleConfirm();
		}
		handleKeyDown(e);
	}

	// Handle Enter as Cancel, Escape as Cancel
	function handleKeyDownCancel(e) {
		if (e.key === 'Enter') {
			handleCancel();
		}
		handleKeyDown(e);
	}

	// Handle Escape as Cancel
	function handleKeyDown(e) {
		if (e.key === 'Escape') {
			handleCancel();
		}
	}

	// Key handling for quantity input
	function handleKeydownQty(e) {
		switch (e.key) {
			case 'Enter':
				handleConfirm();
				break;
			case 'Escape':
				handleCancel();
				break;
			case 'ArrowUp':
				setQty(parseInt(qty) + 1);
				e.preventDefault();
				break;
			case 'ArrowDown': {
				let updQty = parseInt(qty) - 1;
				updQty = updQty > 0 ? updQty : 1;
				setQty(updQty);
				e.preventDefault();
				break;
			}
		}
	}

	function handleConfirm() {
		// Restrict finish to supported only
		let finishUpd = finish;
		let unsupportedFinish = false;
		switch (finish) {
			case cardFinish.nonfoil:
				unsupportedFinish = card.nonfoil ? false : true;
				break;
			case cardFinish.foil:
				unsupportedFinish = card.foil ? false : true;
				break;
			case cardFinish.etched:
				unsupportedFinish = card.etched ? false : true;
				break;
			default:
				break;
		}
		// If selected finish not supported by the card => select supported
		if (unsupportedFinish) {
			if (card.nonfoil) {
				finishUpd = cardFinish.nonfoil;
			} else if (card.foil) {
				finishUpd = cardFinish.foil;
			} else if (card.etched) {
				finishUpd = cardFinish.etched;
			} else {
				console.error('Selected unsupported finish: ', finish);
				return;
			}
		}
		// Prepare positive integer quantity
		let qtyUpd = normalizeQty(qty);
		qtyUpd = qtyUpd > 0 ? qtyUpd : 1;

		// Perform confirmation
		onConfirm(qtyUpd, condition, finishUpd, note, language, merge);
	}

	function handleCancel() {
		onCancel?.();
	}
};

EditSampleBlock.propTypes = {
	// Card for this sample
	card: PropTypes.object.isRequired,
	// Selected sample (may be null)
	sample: PropTypesCustom.object_or_null_required,
	// Default language (if sample is null, e.g. when adding new sample)
	language_default: PropTypes.string.isRequired,
	// Show merge checkbox
	showMerge: PropTypes.bool,
	// Callback function on confirm sample editing
	onConfirm: PropTypes.func.isRequired,
	// Callback function on cancel sample editing
	onCancel: PropTypes.func.isRequired,
};

export default EditSampleBlock;
